import { getApiBaseUrl } from "../utils/ssoProvider";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/axiosInstance.js";
import { openTemplateConflictModal } from "./templateConflictModalSlice.js";

const API_URL = getApiBaseUrl();

// The backend detects conflicts for a saved template via a background Celery task
// (app.templates.services.TemplateService._maybe_trigger_conflict_scan -> "delay"s
// app.template_conflicts.tasks.scan_template_conflicts_for_store) - it is NOT done by the
// time the create/update request returns. There's no push channel for "the scan finished",
// so this polls the list endpoint for a bit, filtered to the saved template's id.
const CONFLICT_POLL_INTERVAL_MS = 4000;
const CONFLICT_POLL_MAX_ATTEMPTS = 15; // ~1 minute total before giving up

/**
 * Poll for an unresolved conflict on the just-saved template, then open the global
 * resolution modal with real data once (if) one shows up. Deliberately not a thunk -
 * it's a detached background watch that must keep running after CreateTemplatesPage
 * unmounts (e.g. the user already navigated to /templates-page), so the modal can pop up
 * "anytime, any screen" per the intended UX.
 */
export async function watchForTemplateConflict(dispatch, { templateId, templateName, templateDescription }) {
  if (!templateId) return;

  for (let attempt = 0; attempt < CONFLICT_POLL_MAX_ATTEMPTS; attempt++) {
    await new Promise(resolve => setTimeout(resolve, CONFLICT_POLL_INTERVAL_MS));

    try {
      const response = await axiosInstance.get(`${API_URL}/v1/store/draft/template-conflicts`, {
        params: { status_filter: "unresolved", template_id: templateId, page_size: 1 },
      });
      const found = response.data?.items?.[0];
      if (!found) continue;

      const otherId = found.template_a_id === templateId ? found.template_b_id : found.template_a_id;
      if (!otherId) continue; // the other side was since deleted - nothing to show

      const otherResponse = await axiosInstance.get(`${API_URL}/v1/store/draft/templates/${otherId}`);

      dispatch(fetchTemplateConflicts()); // refresh the banner/badge with this new conflict too
      dispatch(
        openTemplateConflictModal({
          conflictId: found.id,
          overlapPercent: found.overlap_percentage,
          templateA: {
            id: templateId,
            name: templateName,
            description: templateDescription || "",
          },
          templateB: {
            id: otherId,
            name: otherResponse.data?.name || "Untitled template",
            description: otherResponse.data?.description || "",
          },
        })
      );
      return;
    } catch (error) {
      console.error("Failed to poll for template conflicts:", error);
    }
  }
}

// Global state for template-conflict detection (see components/TemplatePageComponents/GlobalTemplateConflictBanner.jsx).
// Lives in Redux - rather than as page-local state - so the banner can render on every screen via
// ContentLayout regardless of which route the user is on, not just the Templates page.
export const fetchTemplateConflicts = createAsyncThunk("templateConflicts/fetchTemplateConflicts", async () => {
  const response = await axiosInstance.get(`${API_URL}/v1/store/draft/template-conflicts`, {
    params: { status_filter: "unresolved", page_size: 100 },
  });
  const items = response.data?.items ?? [];
  // No titles/descriptions here - the banner only ever shows a generalized count, never
  // which templates are involved. templateIds is kept so other pages (e.g. TemplatesPage's
  // per-row "Conflict" badge) can still tell WHICH rows are conflicting, matched by the
  // real template id rather than by title text.
  return items.map(item => ({ id: item.id, templateIds: [item.template_a_id, item.template_b_id] }));
});

const templateConflictsSlice = createSlice({
  name: "templateConflicts",
  initialState: {
    conflicts: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchTemplateConflicts.fulfilled, (state, action) => {
        state.conflicts = action.payload;
      })
      .addCase(fetchTemplateConflicts.rejected, state => {
        // Silently fail - the banner just doesn't show if the list can't be fetched.
        state.conflicts = [];
      });
  },
});

export default templateConflictsSlice.reducer;
